import {
  readPreviousAnswer,
  isTextAnswer,
  type AnswerJson,
} from "../answerJson";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";

export type TextHighlighterRaw = {
  type: "text_highlighter";
  content: string;
  extra_text: string;
  color: string;
  previous_response?: string[] | string;
  previous_answer_json?: unknown;
  error_message?: string;
  dont_know_option?: { value: string; text: string } | null;
};

export type TextHighlighterDefinition = {
  prompt: string;
  text: string;
  color: string;
  previousResponses: readonly string[];
  previousDontKnow: boolean;
  error?: string;
  dontKnow?: { value: string; label: string };
};

export type HighlightRange = { start: number; end: number };
export type TextHighlighterDraft = {
  ranges: readonly HighlightRange[];
  dontKnowSelected: boolean;
};
export type TextHighlighterEvent =
  | {
      type: "toggle-range";
      start: number;
      end: number;
    }
  | { type: "toggle-dont-know" };
export type TextHighlighterResponse = {
  type: "text_highlighter";
  value: string;
  responses: string;
  answerJson: AnswerJson;
};

const snapRange = (
  text: string,
  start: number,
  end: number,
): HighlightRange | null => {
  if (
    !Number.isInteger(start) ||
    !Number.isInteger(end) ||
    start < 0 ||
    end > text.length ||
    start >= end
  )
    return null;
  let left = start;
  while (left < end && /\s/.test(text[left])) left++;
  let right = end;
  while (right > left && /\s/.test(text[right - 1])) right--;
  if (left >= right) return null;
  while (left > 0 && !/\s/.test(text[left - 1])) left--;
  while (right < text.length && !/\s/.test(text[right])) right++;
  return { start: left, end: right };
};

const validRanges = (text: string, ranges: readonly HighlightRange[]) => {
  if (ranges.length === 0) return false;
  let lastEnd = 0;
  return ranges.every(({ start, end }) => {
    if (
      !Number.isInteger(start) ||
      !Number.isInteger(end) ||
      start < lastEnd ||
      start < 0 ||
      end > text.length ||
      start >= end ||
      !text.slice(start, end).trim()
    )
      return false;
    lastEnd = end;
    return true;
  });
};

const restoreRanges = (
  text: string,
  values: readonly string[],
): HighlightRange[] => {
  const ranges: HighlightRange[] = [];
  for (const value of values) {
    if (!value) continue;
    let start = text.indexOf(value);
    while (
      start >= 0 &&
      ranges.some(
        (range) => range.end > start && range.start < start + value.length,
      )
    ) {
      start = text.indexOf(value, start + 1);
    }
    if (start >= 0) ranges.push({ start, end: start + value.length });
  }
  return ranges.sort((a, b) => a.start - b.start);
};

export const textHighlighterCore: QuestionCore<
  TextHighlighterRaw,
  TextHighlighterDefinition,
  TextHighlighterDraft,
  TextHighlighterEvent,
  TextHighlighterResponse
> = {
  decode: (raw) => {
    raw = {
      ...raw,
      previous_response: readPreviousAnswer(
        raw.previous_answer_json,
        raw.previous_response,
        isTextAnswer,
      ),
    };
    if (
      typeof raw.extra_text !== "string" ||
      !/^#[0-9A-Fa-f]{6}$/.test(raw.color)
    ) {
      throw new Error(
        "Text highlighter requires source text and a six-digit color.",
      );
    }
    if (
      raw.previous_response !== undefined &&
      ((typeof raw.previous_response === "string" &&
        raw.previous_response !== raw.dont_know_option?.value) ||
        (typeof raw.previous_response !== "string" &&
          (!Array.isArray(raw.previous_response) ||
            raw.previous_response.some((value) => typeof value !== "string"))))
    ) {
      throw new Error(
        "Text highlighter previous response must be a string or list of strings.",
      );
    }
    const dontKnowValue = raw.dont_know_option?.value;
    const previousDontKnow =
      dontKnowValue !== undefined &&
      (raw.previous_response === dontKnowValue ||
        (Array.isArray(raw.previous_response) &&
          raw.previous_response.length === 1 &&
          raw.previous_response[0] === dontKnowValue));
    return {
      prompt: raw.content,
      text: raw.extra_text,
      color: raw.color,
      previousResponses:
        !previousDontKnow && Array.isArray(raw.previous_response)
          ? raw.previous_response
          : [],
      previousDontKnow,
      ...(raw.error_message ? { error: raw.error_message } : {}),
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
    };
  },
  initialize: (definition) => ({
    ranges: restoreRanges(definition.text, definition.previousResponses),
    dontKnowSelected: definition.previousDontKnow,
  }),
  update: (definition, draft, event) => {
    if (event.type === "toggle-dont-know") {
      return {
        ranges: [],
        dontKnowSelected: !draft.dontKnowSelected,
      };
    }
    const next = snapRange(definition.text, event.start, event.end);
    if (!next) return draft;
    const exact = draft.ranges.find(
      (range) => range.start === next.start && range.end === next.end,
    );
    if (exact)
      return {
        ranges: draft.ranges.filter((range) => range !== exact),
        dontKnowSelected: false,
      };
    const overlap = draft.ranges.filter(
      (range) => range.end > next.start && range.start < next.end,
    );
    const merged = {
      start: Math.min(next.start, ...overlap.map((range) => range.start)),
      end: Math.max(next.end, ...overlap.map((range) => range.end)),
    };
    return {
      ranges: [
        ...draft.ranges.filter((range) => !overlap.includes(range)),
        merged,
      ].sort((a, b) => a.start - b.start),
      dontKnowSelected: false,
    };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) =>
    (draft.dontKnowSelected && Boolean(definition.dontKnow)) ||
    validRanges(definition.text, draft.ranges)
      ? { valid: true }
      : { valid: false, error: copy.validation.highlight },
  encode: (definition, draft) => {
    const validation = textHighlighterCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    const values =
      draft.dontKnowSelected && definition.dontKnow
        ? [definition.dontKnow.value]
        : draft.ranges.map(({ start, end }) =>
            definition.text.slice(start, end),
          );
    return {
      type: "text_highlighter",
      value: values.join(", "),
      responses: encodeResponses(values),
      answerJson: values,
    };
  },
  projectTranscript: (definition, draft) => {
    const validation = textHighlighterCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      kind: "text_highlighter",
      question: definition.prompt,
      answer:
        draft.dontKnowSelected && definition.dontKnow
          ? definition.dontKnow.label
          : draft.ranges
              .map(({ start, end }) => definition.text.slice(start, end))
              .join(", "),
    };
  },
};
